import clsx from "clsx";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  DEVICE_OPTIONS,
  DRAWER_TRANSITION_MS,
  COUNTRY_OPTIONS,
  KIND_OPTIONS,
} from "./filters/constants";
import { DictMultiSelectField } from "./filters/DictMultiSelectField";
import { FilterSection, MultiSelectField } from "./filters/fields";
import type {
  ChoiceOption,
  NumberFilterField,
  StringFilterField,
} from "./filters/types";
import {
  getUniqueCampaigns,
  getUniqueCategories,
  getUniqueChannels,
} from "./filters/utils";
import type {
  AnalyticsCampaignOption,
  AnalyticsCategoryOption,
  AnalyticsChannelOption,
  AnalyticsFilters,
} from "@/features/analytics/types";

type AnalyticsFiltersDrawerProps = {
  isOpen: boolean;
  value: AnalyticsFilters;
  campaigns: AnalyticsCampaignOption[];
  channels: AnalyticsChannelOption[];
  categories: AnalyticsCategoryOption[];
  tags?: string[];
  onChange: (nextValue: AnalyticsFilters) => void;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
};

const CHANNEL_KIND_LABELS: Record<AnalyticsChannelOption["kind"], string> = {
  static: "Static",
  dynamic: "Dynamic",
  overlay: "Overlay",
  banner: "Banner",
};

const CloseIcon = (): JSX.Element => {
  return (
    <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 20 20" fill="none">
      <path
        d="M5 5L15 15M15 5L5 15"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
};

export const AnalyticsFiltersDrawer = ({
  isOpen,
  value,
  campaigns,
  channels,
  categories,
  tags,
  onChange,
  onClose,
  onApply,
  onReset,
}: AnalyticsFiltersDrawerProps): JSX.Element | null => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);
  const uniqueCampaigns = getUniqueCampaigns(campaigns);
  const uniqueChannels = getUniqueChannels(channels);
  const uniqueCategories = getUniqueCategories(categories);
  const campaignOptions = uniqueCampaigns.map((campaign) => ({
    value: campaign.uuid,
    label: `${campaign.name} • ${campaign.uuid}`,
  }));
  const channelOptions = uniqueChannels.map((channel) => ({
    value: channel.id,
    label: `${channel.name} • ${CHANNEL_KIND_LABELS[channel.kind]}`,
  }));
  const categoryOptions = uniqueCategories.map((category) => ({
    value: category.id,
    label: `${category.name} • ${category.id}`,
  }));
  const tagOptions = (tags ?? []).map((tag) => ({ value: tag, label: tag }));
  const showTagsSection = tags !== undefined;

  useEffect(() => {
    if (isOpen) {
      setIsVisible(false);
      setShouldRender(true);
      return undefined;
    }

    setIsVisible(false);

    if (!shouldRender) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldRender(false);
    }, DRAWER_TRANSITION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender || !isOpen) {
      return undefined;
    }

    let firstFrameId = 0;
    let secondFrameId = 0;

    firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrameId);
      window.cancelAnimationFrame(secondFrameId);
    };
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, shouldRender]);

  if (!shouldRender || typeof document === "undefined") {
    return null;
  }

  const updateFilters = (nextValue: AnalyticsFilters): void => {
    onChange(nextValue);
  };

  const updateStringListField = (
    field: StringFilterField,
    nextValue: string[],
  ): void => {
    updateFilters({
      ...value,
      [field]: nextValue,
    });
  };

  const updateNumberListField = (
    field: NumberFilterField,
    nextValue: number[],
  ): void => {
    updateFilters({
      ...value,
      [field]: nextValue,
    });
  };

  const updateDictField = (
    field: "region" | "city" | "platform",
    nextValue: ChoiceOption<number>[],
  ): void => {
    updateFilters({
      ...value,
      [field]: nextValue,
    });
  };

  const activeFiltersCount = Object.values(value).reduce((count, items) => {
    return items.length > 0 ? count + 1 : count;
  }, 0);

  return createPortal(
    <div
      className={clsx("fixed inset-0 z-[1400]", {
        "pointer-events-auto": shouldRender,
        "pointer-events-none": !shouldRender,
      })}
    >
      <button
        type="button"
        aria-label="Close filters drawer"
        onClick={onClose}
        className={clsx(
          "absolute inset-0 border-0 bg-[rgba(2,6,23,0.62)] p-0 transition-opacity duration-[220ms] ease-out",
          isVisible ? "opacity-100" : "opacity-0",
        )}
      />

      <aside
        aria-modal="true"
        role="dialog"
        className={clsx(
          "absolute right-0 top-0 flex h-full w-full max-w-[440px] flex-col overflow-hidden bg-(--surface-1) will-change-transform transition-transform duration-[220ms] ease-out",
          isVisible ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-5">
          <div>
            <h2 className="text-[1.05rem] font-semibold text-(--text-primary)">
              Filters
            </h2>
            <p className="mt-0.5 text-[0.78rem] text-(--text-muted)">
              {activeFiltersCount} active
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="inline-flex h-8 w-8 items-center justify-center rounded-(--radius-md) border-0 bg-transparent text-(--text-muted) transition hover:bg-(--surface-2) hover:text-(--text-primary)"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <FilterSection
            title="Geography"
            description="Filter the report by country, region, or city."
          >
            <div className="space-y-4">
              <MultiSelectField
                label="Countries"
                helperText="Multi-select"
                options={COUNTRY_OPTIONS}
                value={value.country}
                onChange={(nextValue) =>
                  updateStringListField("country", nextValue)
                }
                placeholder="Search countries..."
              />

              <DictMultiSelectField
                label="Regions"
                dictType="regions"
                value={value.region}
                onChange={(nextValue) => updateDictField("region", nextValue)}
                placeholder="Search regions..."
              />

              <DictMultiSelectField
                label="Cities"
                dictType="cities"
                value={value.city}
                onChange={(nextValue) => updateDictField("city", nextValue)}
                placeholder="Search cities..."
              />
            </div>
          </FilterSection>

          <FilterSection
            title="Platform & Device"
            description="Narrow results by platform, device type, and inventory format."
          >
            <div className="space-y-4">
              <DictMultiSelectField
                label="Platforms"
                dictType="platforms"
                value={value.platform}
                onChange={(nextValue) => updateDictField("platform", nextValue)}
                placeholder="Search platforms..."
              />

              <MultiSelectField
                label="Device Types"
                helperText="Multi-select"
                options={DEVICE_OPTIONS}
                value={value.deviceType}
                onChange={(nextValue) =>
                  updateNumberListField("deviceType", nextValue)
                }
                placeholder="Search device types..."
              />

              <MultiSelectField
                label="Inventory Kind"
                helperText="Multi-select"
                options={KIND_OPTIONS}
                value={value.kind}
                onChange={(nextValue) =>
                  updateNumberListField("kind", nextValue)
                }
                placeholder="Search inventory kinds..."
              />
            </div>
          </FilterSection>

          <FilterSection
            title="Campaigns"
            description="Limit the report to one or more campaigns."
          >
            {campaignOptions.length > 0 ? (
              <MultiSelectField
                label="Campaigns"
                helperText="Multi-select"
                options={campaignOptions}
                value={value.campaignUuid}
                onChange={(nextValue) =>
                  updateFilters({
                    ...value,
                    campaignUuid: nextValue,
                  })
                }
                placeholder="Search campaigns..."
              />
            ) : (
              <p className="text-[0.82rem] text-(--text-secondary)">
                No campaigns available.
              </p>
            )}
          </FilterSection>

          <FilterSection
            title="Channels"
            description="Focus on one or more channels."
          >
            {channelOptions.length > 0 ? (
              <MultiSelectField
                label="Channels"
                helperText="Multi-select"
                options={channelOptions}
                value={value.channelId}
                onChange={(nextValue) =>
                  updateNumberListField("channelId", nextValue)
                }
                placeholder="Search channels..."
              />
            ) : (
              <p className="text-[0.82rem] text-(--text-secondary)">
                No channel options available.
              </p>
            )}
          </FilterSection>

          <FilterSection
            title="Categories"
            description="Filter by one or more content categories."
          >
            {categoryOptions.length > 0 ? (
              <MultiSelectField
                label="Categories"
                helperText="Multi-select"
                options={categoryOptions}
                value={value.categories}
                onChange={(nextValue) =>
                  updateStringListField("categories", nextValue)
                }
                placeholder="Search categories..."
              />
            ) : (
              <p className="text-[0.82rem] text-(--text-secondary)">
                No category options available.
              </p>
            )}
          </FilterSection>

          {showTagsSection ? (
            <FilterSection
              title="Tags"
              description="Filter by tags assigned to devices."
            >
              {tagOptions.length > 0 ? (
                <MultiSelectField
                  label="Tags"
                  helperText="Multi-select"
                  options={tagOptions}
                  value={value.tags}
                  onChange={(nextValue) =>
                    updateStringListField("tags", nextValue)
                  }
                  placeholder="Search tags..."
                />
              ) : (
                <p className="text-[0.82rem] text-(--text-secondary)">
                  No tags assigned to devices yet.
                </p>
              )}
            </FilterSection>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-(--border-subtle) px-5 py-4">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex h-10 items-center justify-center rounded-(--radius-md) border-0 bg-transparent px-3 text-[13px] font-semibold text-(--text-muted) transition hover:text-(--text-primary)"
          >
            Reset
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 items-center justify-center rounded-(--radius-md) border-0 bg-transparent px-3 text-[13px] font-semibold text-(--text-muted) transition hover:text-(--text-primary)"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="inline-flex h-10 items-center justify-center rounded-(--radius-md) border-0 bg-(--brand-primary) px-4 text-[13px] font-bold text-(--brand-primary-contrast) transition hover:bg-(--brand-primary-hover)"
            >
              Apply
            </button>
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
};
